import { ref, computed } from "vue";
import {
	API_ERROR_CODES,
	ApiCallError,
	configureAuth,
	createSession,
	logout as apiLogout,
} from "@/api";
import type { SessionUser } from "@/api";
import { setLocale } from "@/i18n";
import { onSocketEvent } from "@/socket";
import { clearByPurpose } from "@/shared/utils/storage";

/*
  The access token and user live only in module memory. A cookie-bound server
  session is the durable credential; the token is re-minted from it on every
  page load, so nothing bearer-shaped is ever written to Web Storage.
*/
const accessToken = ref<string | null>(null);
const user = ref<SessionUser | null>(null);
const authError = ref<string | null>(null);
// This page load is the return from a sign-in that did not finish (`?auth_silent=` or
// `?auth_error=`). Asking the provider again would bring the same answer straight back, so
// silent sign-in waits for the next load.
let returnedFromSignin = false;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<boolean> | null = null;

const SESSION_URL_BASE = "/api/v2/auth";

export function currentAccessToken(): string | null {
	return accessToken.value;
}

function applyUserLocale(u: SessionUser | null) {
	if (u?.language === "en" || u?.language === "de") {
		setLocale(u.language);
	}
}

function scheduleRefresh(expiresInSeconds: number) {
	if (refreshTimer) clearTimeout(refreshTimer);
	refreshTimer = setTimeout(() => {
		void ensureSession();
	}, expiresInSeconds * 800);
}

/*
  VIEW STATE GOES, PREFERENCES STAY.

  Sign-out used to remove the token and nothing else, which left every other
  key on the device for whoever signed in next. On a shared machine that
  means the next user inherits the previous one's searches and filters —
  and a search term can name work they have no business seeing.

  Theme, locale and pane widths are device ergonomics with no personal
  content in them, and wiping those on every sign-out is a papercut on the
  single-user machines that are the common case. The Local data panel in
  Account clears them explicitly for anyone who wants that.
*/
function clearState() {
	if (refreshTimer) clearTimeout(refreshTimer);
	refreshTimer = null;
	accessToken.value = null;
	user.value = null;
	clearByPurpose("essential", "view-state");
}

async function fetchSession(): Promise<boolean> {
	try {
		const res = await createSession();
		accessToken.value = res.access_token;
		user.value = res.user;
		applyUserLocale(res.user);
		scheduleRefresh(res.expires_in);
		return true;
	} catch (e) {
		// 401 or network failure: report signed-out and do not retry here, so a
		// dead session cannot spin in a loop.
		accessToken.value = null;
		user.value = null;
		if (refreshTimer) clearTimeout(refreshTimer);
		refreshTimer = null;
		if (e instanceof ApiCallError) {
			// The server no longer holds the session this browser named (swept as unused), or the
			// provider would not renew it. Go back through the provider: while its own session lasts
			// it signs in again without a prompt, and otherwise asks. The response cleared the
			// cookie, so an abandoned sign-in does not bring this back.
			if (e.code === API_ERROR_CODES.sessionLost || e.code === API_ERROR_CODES.sessionEnded) {
				login();
			}
			// The server runs silent sign-in: ask the provider whether this browser is signed in
			// there. It comes back signed in, or to this page marked `auth_silent=declined`, and
			// that load does not ask again.
			else if (e.code === API_ERROR_CODES.silentSignin && !returnedFromSignin) {
				login(undefined, { silent: true });
			}
		}
		return false;
	}
}

/** Resolves true when a session exists. Concurrent callers share one request. */
export function ensureSession(): Promise<boolean> {
	inFlight ??= fetchSession().finally(() => {
		inFlight = null;
	});
	return inFlight;
}

// Set once a sign-in redirect has started. If the server keeps refusing a fresh
// token (e.g. clock skew), every 401 or socket refusal would otherwise send the page
// to the IdP again; one redirect per page load is the ceiling. Module memory, not
// Web Storage, so a reload starts clean.
let loginStarted = false;

/**
 * Navigates the whole window to the server-side sign-in, at most once per page load; `returnTo`
 * defaults to the current path. `silent` asks the provider not to prompt: it signs in from its own
 * session, or sends the browser back to `returnTo` unchanged.
 */
export function login(returnTo?: string, { silent = false }: { silent?: boolean } = {}): void {
	if (loginStarted) return;
	loginStarted = true;
	const target =
		returnTo ?? window.location.pathname + window.location.search + window.location.hash;
	const query = `return_to=${encodeURIComponent(target)}${silent ? "&silent=true" : ""}`;
	window.location.assign(`${SESSION_URL_BASE}/login?${query}`);
}

export async function logout(): Promise<void> {
	let endSessionUrl: string | null = null;
	try {
		endSessionUrl = (await apiLogout()).end_session_url;
	} catch {
		// Session may already be gone; still clear local state.
	}
	clearState();
	window.location.assign(endSessionUrl ?? "/");
}

/** Reads `?auth_error=` and `?auth_silent=` once and strips them from the address bar without a reload. */
function consumeSigninResult() {
	const url = new URL(window.location.href);
	const code = url.searchParams.get("auth_error");
	if (!code && !url.searchParams.has("auth_silent")) return;
	returnedFromSignin = true;
	if (code) authError.value = code;
	url.searchParams.delete("auth_error");
	url.searchParams.delete("auth_silent");
	window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
}

configureAuth({
	getToken: currentAccessToken,
	onUnauthorized: async () => {
		if (await ensureSession()) return true;
		login();
		return false;
	},
});
consumeSigninResult();

/**
 * The user-room events about the account itself, and what each reaches — the shared counterpart of
 * the Designer's `USER_EVENT_HANDLERS`, checked by `npm run lint:sync` the same way.
 */
export const ACCOUNT_EVENT_HANDLERS: Record<string, readonly "profile"[]> = {
	"account:access:changed": ["profile"],
};

/*
  A system role changed in the control center: what the session says this user may do is stale.
  Renewed, it offers or withdraws the control center. A ban is the case where it cannot be
  renewed — the server refuses the session from now on and closes the socket right after this
  event — and the session ends here rather than at whichever request fails next: the sign-in it
  goes back to answers with the suspension.
*/
for (const event of Object.keys(ACCOUNT_EVENT_HANDLERS)) {
	onSocketEvent(event, () => {
		void ensureSession().then((ok) => {
			if (!ok) login();
		});
	});
}

export function useAuth() {
	const isAuthenticated = computed(() => !!accessToken.value);

	// The server re-reads userinfo whenever it refreshes the token, so a session
	// round-trip is the profile refresh.
	async function refreshProfile() {
		await ensureSession();
	}

	/**
	 * What the server's license switches on, from the session — `null` until it loads, and while the
	 * server has no confirmed license. What the client OFFERS follows it (a picker's "Personal", the
	 * member limit said where people are added); the server decides on every request regardless.
	 */
	const licenseFeatures = computed(() => user.value?.license_features ?? null);

	return {
		user,
		isAuthenticated,
		licenseFeatures,
		ensureSession,
		login,
		logout,
		authError,
		refreshProfile,
	};
}
